import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ShieldCheck, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE = 10 * 1024 * 1024; // 10 MB

// Field schema mirrors backend INTAKE_SECTIONS
const SECTIONS = [
  {
    title: "Personal information",
    overline: "About you",
    fields: [
      { key: "client_full_name", label: "Client's full name", required: true, placeholder: "First Middle Last" },
      { key: "middle_name_on_id", label: "Middle name on ID (if different)", required: false },
      { key: "date_of_birth", label: "Date of birth", required: true, type: "date" },
      { key: "ssn", label: "SSN", required: true, placeholder: "XXX-XX-XXXX", sensitive: true },
      { key: "gender", label: "Gender", required: true, type: "select", options: ["Male", "Female"] },
      { key: "drivers_license_number", label: "Driver's License #", required: true },
      { key: "drivers_license_expiration", label: "Driver's License expiration", required: true, type: "date" },
      { key: "height", label: "Height", required: true, placeholder: "e.g. 5'10\"" },
      { key: "weight", label: "Weight", required: true, placeholder: "e.g. 175 lbs" },
      { key: "home_address", label: "Home address", required: true, span: 2, placeholder: "Street, City, State, ZIP" },
      { key: "years_at_address", label: "Years at this address", required: true },
      { key: "phone", label: "Phone number", required: true, type: "tel" },
      { key: "email", label: "Email", required: true, type: "email", span: 2 },
      { key: "state_country_of_birth", label: "State/Country of birth", required: true },
      { key: "marital_status", label: "Marital status", required: true, type: "select", options: ["Single", "Married", "Divorced", "Other"] },
      { key: "us_citizen", label: "US Citizen", required: true, type: "radio", options: ["Yes", "No"] },
    ],
  },
  {
    title: "Employment",
    overline: "Where you work",
    fields: [
      { key: "employer_name_type", label: "Employer name & type of business", required: true, span: 2 },
      { key: "employment_length", label: "How long employed there" , required: true },
      { key: "position", label: "Position / title", required: true },
      { key: "employer_address", label: "Employer full address", required: true, span: 2 },
      { key: "annual_income", label: "Annual income", required: true, placeholder: "$" },
      { key: "net_worth", label: "Net worth", required: true, placeholder: "$" },
    ],
  },
  {
    title: "Doctor",
    overline: "Your primary care",
    fields: [
      { key: "doctor_name", label: "Primary doctor or practice name", required: true, span: 2 },
      { key: "doctor_phone", label: "Doctor phone", required: true, type: "tel" },
      { key: "last_visit_date", label: "Date of last visit", required: true, type: "date" },
      { key: "doctor_address", label: "Doctor address", required: true, span: 2 },
      { key: "last_visit_reason_results", label: "Reason and results of last visit", required: true, span: 2, type: "textarea" },
    ],
  },
  {
    title: "Legal",
    fields: [
      { key: "felonies_misdemeanors_dui", label: "Any felonies, misdemeanors or DUIs?", required: true, type: "radio", options: ["Yes", "No"], span: 2 },
    ],
  },
  {
    title: "Primary beneficiary",
    overline: "Who receives the policy",
    description: "We need the beneficiary's SSN to verify identity, avoid payout delays, and for tax reporting.",
    fields: [
      { key: "primary_beneficiary_name_pct", label: "Full name & % (e.g. Jane Doe 100%)", required: true, span: 2 },
      { key: "primary_beneficiary_ssn", label: "Primary beneficiary SSN", required: true, sensitive: true },
      { key: "primary_beneficiary_dob", label: "Date of birth", required: true, type: "date" },
      { key: "primary_beneficiary_relationship", label: "Relationship to policy owner", required: true, span: 2 },
    ],
  },
  {
    title: "Second primary beneficiary",
    fields: [
      { key: "second_beneficiary_name_pct", label: "Full name & %", required: true, span: 2 },
      { key: "second_beneficiary_dob", label: "Date of birth", required: true, type: "date" },
      { key: "second_beneficiary_relationship", label: "Relationship to insured", required: true },
    ],
  },
  {
    title: "Contingent beneficiaries",
    description: "Receive the benefit if the primary beneficiary passes before them.",
    fields: [
      { key: "contingent_beneficiary_1", label: "Contingent beneficiary #1", required: true, span: 2 },
      { key: "contingent_beneficiary_2", label: "Contingent beneficiary #2", required: true, span: 2 },
    ],
  },
  {
    title: "Banking (optional)",
    overline: "For premium auto-pay",
    description: "Leave blank if you'd rather provide this later with your agent.",
    fields: [
      { key: "bank_name", label: "Financial institution name" },
      { key: "bank_account_name", label: "Name on account" },
      { key: "bank_account_number", label: "Account number", sensitive: true },
      { key: "bank_routing_number", label: "Routing number", sensitive: true },
    ],
  },
  {
    title: "Health & lifestyle",
    fields: [
      { key: "tobacco_history", label: "Tobacco / nicotine history (cigarettes, cigars, vape, patches, gum, hookah, etc.)", required: true, span: 2, type: "textarea" },
      { key: "siblings", label: "Siblings — how many, alive/passed (type N/A if none)", required: true, span: 2, type: "textarea" },
      { key: "parents_status", label: "Parents — ages if living, cause of death if passed", required: true, span: 2, type: "textarea" },
      { key: "medical_conditions_medications", label: "Major or minor medical issues + medications", required: true, span: 2, type: "textarea" },
      { key: "regular_medications", label: "Regular medications (incl. OTC)", required: true, span: 2, type: "textarea" },
      { key: "alcohol_use", label: "Alcohol use (light / moderate / heavy)", required: true, span: 2 },
    ],
  },
  {
    title: "Travel",
    fields: [
      { key: "travel_next_12_months", label: "Airline tickets out of US in the next 12 months? Where & when?", required: true, span: 2, type: "textarea" },
    ],
  },
  {
    title: "Medical exam scheduling",
    description: "Most policies require a quick paramedical exam. 8-hour fast required beforehand.",
    fields: [
      { key: "medical_exam_availability", label: "Best day & morning time in the next 5 days", required: true, span: 2 },
    ],
  },
  {
    title: "Referring agent",
    fields: [
      { key: "agent_name", label: "Name of agent who sent you this link", required: true, span: 2 },
    ],
  },
];

function initialForm() {
  const f = {};
  SECTIONS.forEach((s) => s.fields.forEach((field) => { f[field.key] = ""; }));
  return f;
}

export default function IntakePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [idFile, setIdFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setIdFile(null); return; }
    if (f.size > MAX_FILE) {
      toast.error("File is larger than 10 MB. Please upload a smaller file.");
      e.target.value = "";
      return;
    }
    setIdFile(f);
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const validate = () => {
    for (const section of SECTIONS) {
      for (const field of section.fields) {
        if (field.required && !String(form[field.key] || "").trim()) {
          toast.error(`Missing: ${field.label}`);
          document.getElementById(`intake-${field.key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
          return false;
        }
      }
    }
    if (!idFile) {
      toast.error("Please upload a copy of your ID before submitting.");
      document.getElementById("intake-id-file")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data_base64 = await fileToBase64(idFile);
      const payload = {
        ...form,
        id_file: {
          filename: idFile.name,
          content_type: idFile.type || "application/octet-stream",
          data_base64,
        },
      };
      await axios.post(`${API}/intake`, payload);
      setSubmitted(true);
      toast.success("Intake submitted. Your agent has been notified.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again or contact your agent.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-20">
        <div className="max-w-xl text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-accent/15 flex items-center justify-center mb-8">
            <CheckCircle2 className="h-8 w-8 text-accent" strokeWidth={1.5} />
          </div>
          <p className="overline mb-3">Thank you</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-primary leading-tight mb-6">
            Your intake is in good hands.
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-10">
            Your licensed Faith Family Finance agent has been notified and will reach out to confirm your medical exam time
            and walk you through the next steps. Most clients are approved within 2–7 days.
          </p>
          <Button onClick={() => navigate("/")} className="rounded-full h-12 px-8 bg-primary hover:bg-primary/90">
            Return home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      <header className="border-b border-border/60 bg-background/95 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="block">
            <Logo variant="wordmark" />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 sm:pt-12 lg:pt-20">
        <p className="overline mb-4">Adult client intake</p>
        <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight mb-8">
          Let&rsquo;s get your policy<br />
          <em className="not-italic text-accent">underwritten.</em>
        </h1>

        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-5 mb-12 flex gap-4 items-start">
          <ShieldCheck className="h-5 w-5 text-accent mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="text-sm leading-relaxed text-foreground/85">
            <strong>Your privacy is our priority.</strong> We do not share or sell your data; it is only shared with insurers as needed.
            Submitting this application does not guarantee approval, and no payments will be processed without your review and consent to your agent.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-12" data-testid="intake-form">
          {SECTIONS.map((section, sIdx) => (
            <section
              key={section.title}
              className="rounded-2xl sm:rounded-3xl bg-card border border-border/60 p-5 sm:p-8 lg:p-10 soft-shadow"
              data-testid={`intake-section-${sIdx}`}
            >
              <div className="flex items-baseline gap-4 mb-2">
                <span className="font-serif text-3xl italic text-accent">{String(sIdx + 1).padStart(2, "0")}</span>
                <div>
                  {section.overline ? <p className="overline">{section.overline}</p> : null}
                  <h2 className="font-serif text-2xl sm:text-3xl text-primary leading-tight">{section.title}</h2>
                </div>
              </div>
              {section.description ? (
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 ml-12">{section.description}</p>
              ) : null}

              <div className="grid sm:grid-cols-2 gap-5 mt-6">
                {section.fields.map((field) => (
                  <FieldRow
                    key={field.key}
                    field={field}
                    value={form[field.key]}
                    onChange={(v) => update(field.key, v)}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* ID upload */}
          <section
            id="intake-id-file"
            className="rounded-2xl sm:rounded-3xl bg-card border border-border/60 p-5 sm:p-8 lg:p-10 soft-shadow"
          >
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-serif text-3xl italic text-accent">{String(SECTIONS.length + 1).padStart(2, "0")}</span>
              <div>
                <p className="overline">Identification</p>
                <h2 className="font-serif text-2xl sm:text-3xl text-primary">Upload your ID</h2>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6 ml-12">
              Clear photo or PDF of your Driver's License, Passport, or other government-issued ID. Max 10 MB.
            </p>

            <label
              htmlFor="intake-id-input"
              className="block rounded-2xl border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 transition-colors p-8 cursor-pointer text-center"
              data-testid="intake-id-dropzone"
            >
              <Upload className="h-6 w-6 text-accent mx-auto mb-3" strokeWidth={1.5} />
              {idFile ? (
                <div>
                  <p className="font-medium text-foreground">{idFile.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(idFile.size / 1024).toFixed(0)} KB · click to change
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-foreground">Click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG · max 10 MB</p>
                </div>
              )}
              <input
                id="intake-id-input"
                data-testid="intake-id-input"
                type="file"
                accept="application/pdf,image/*"
                onChange={onFile}
                className="hidden"
              />
            </label>
          </section>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-4">
            <p className="text-xs text-muted-foreground max-w-md leading-relaxed">
              By submitting, you authorize Faith Family Finance to share this information with insurance carriers solely
              for the purpose of evaluating your application.
            </p>
            <Button
              type="submit"
              data-testid="intake-submit"
              disabled={submitting}
              className="rounded-full h-13 px-10 py-3 bg-primary hover:bg-primary/90 text-base whitespace-nowrap"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </span>
              ) : "Submit intake"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const id = `intake-${field.key}`;
  const span = field.span === 2 ? "sm:col-span-2" : "";

  if (field.type === "radio") {
    return (
      <div id={id} className={span}>
        <Label className="text-sm font-medium text-foreground">
          {field.label}{field.required ? <span className="text-accent"> *</span> : null}
        </Label>
        <RadioGroup
          value={value}
          onValueChange={onChange}
          className="flex gap-6 mt-3"
          data-testid={`intake-${field.key}`}
        >
          {field.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value={opt} id={`${id}-${opt}`} />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </RadioGroup>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div id={id} className={span}>
        <Label className="text-sm font-medium text-foreground" htmlFor={id}>
          {field.label}{field.required ? <span className="text-accent"> *</span> : null}
        </Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id={id} data-testid={`intake-${field.key}`} className="h-12 rounded-xl mt-2">
            <SelectValue placeholder="Choose one" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div id={id} className={span}>
        <Label htmlFor={id} className="text-sm font-medium text-foreground">
          {field.label}{field.required ? <span className="text-accent"> *</span> : null}
        </Label>
        <Textarea
          id={id}
          data-testid={`intake-${field.key}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className="mt-2 min-h-[88px] rounded-xl"
        />
      </div>
    );
  }

  return (
    <div id={id} className={span}>
      <Label htmlFor={id} className="text-sm font-medium text-foreground">
        {field.label}{field.required ? <span className="text-accent"> *</span> : null}
      </Label>
      <Input
        id={id}
        data-testid={`intake-${field.key}`}
        type={field.type || "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        autoComplete={field.sensitive ? "off" : undefined}
        className="mt-2 h-12 rounded-xl"
      />
    </div>
  );
}
